import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { aiInterviewsAPI, errMsg } from '../../../lib/aiInterviewApi';
import { Empty, fmtDate, Loading, Pill, StatusPill, TierPill } from './common';

const ACC_LABEL = { extra_time: 'Extra time', captions: 'Captions', human_interview: 'Human interview', reschedule: 'Reschedule', assistive_technology: 'Assistive technology', other: 'Other' };
const ACC_TONE = { pending: 'amber', approved: 'green', auto_applied: 'teal', denied: 'red', resolved: 'slate' };

const riskBg = (score) => `conic-gradient(${score >= 60 ? '#EF4444' : score >= 25 ? '#F59E0B' : '#10B981'} ${score * 3.6}deg, #EEF2F5 0deg)`;
export const RiskRing = ({ score = 0, size = 84, testId }) => (
  <div className="iv-risk" style={{ width: size, height: size, background: riskBg(score), padding: 6 }} data-testid={testId}>
    <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: '#fff', display: 'grid', placeItems: 'center', fontSize: size > 60 ? '1.5rem' : '0.95rem' }}>{score}</div>
  </div>
);

export function IntegrityQueueTab({ refreshKey }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    aiInterviewsAPI.queue(status).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [status, refreshKey]);

  return (
    <div data-testid="integrity-queue-tab">
      <div className="iv-chips">
        {[['pending', 'Waiting for review'], ['reviewed', 'Reviewed']].map(([k, l]) => (
          <button type="button" key={k} className={`iv-chip ${status === k ? 'is-on' : ''}`} onClick={() => setStatus(k)} data-testid={`queue-filter-${k}`}>{l}</button>
        ))}
      </div>
      <p className="iv-small" style={{ marginBottom: '0.75rem' }}>Signals only route interviews here. A person decides whether there is a concern; nothing is rejected automatically.</p>
      {!rows && <Loading />}
      {rows && !rows.length && <Empty testId="integrity-queue-empty" title={status === 'pending' ? 'Nothing waiting for review' : 'No reviewed interviews yet'} />}
      {rows && rows.length > 0 && (
        <div style={{ display: 'grid', gap: '0.6rem' }}>
          {rows.map((r) => (
            <button type="button" key={r.id} className="iv-card iv-row" style={{ marginBottom: 0, textAlign: 'left', cursor: 'pointer', justifyContent: 'space-between', width: '100%' }}
              onClick={() => navigate(`/ai-interviews/${r.id}?focus=integrity`)} data-testid={`queue-row-${r.id}`}>
              <span className="iv-row" style={{ gap: '0.9rem' }}>
                <RiskRing score={r.risk_score} size={52} />
                <span>
                  <b style={{ color: '#002A38' }}>{r.candidate_name}</b>
                  <span className="iv-small" style={{ display: 'block' }}>{r.job_title} · {fmtDate(r.completed_at || r.created_at)}</span>
                  <span className="iv-small" style={{ display: 'block' }}>{Object.entries(r.signal_counts || {}).slice(0, 4).map(([k, n]) => `${k.replace(/_/g, ' ')} ×${n}`).join(' · ') || 'No signal details'}</span>
                </span>
              </span>
              <span className="iv-row" style={{ gap: '0.4rem' }}>
                <TierPill tier={r.integrity_tier} />
                <StatusPill status={r.status} />
                <Pill tone={r.determination ? 'navy' : 'amber'}>{r.integrity_label}</Pill>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const AccRow = ({ a, onDecided }) => {
  const [note, setNote] = useState('');
  const decide = async (status) => {
    try {
      await aiInterviewsAPI.decideAccommodation(a.id, { status, note });
      toast.success('Accommodation updated');
      onDecided();
    } catch (e) {
      toast.error(errMsg(e, 'Could not update'));
    }
  };
  return (
    <div className="iv-card" style={{ marginBottom: 0 }} data-testid={`accommodation-row-${a.id}`}>
      <div className="iv-row" style={{ justifyContent: 'space-between' }}>
        <span><b style={{ color: '#002A38' }}>{ACC_LABEL[a.type] || a.type}</b> · {a.candidate_name} <span className="iv-small">({a.job_title})</span></span>
        <Pill tone={ACC_TONE[a.status]}>{a.status.replace('_', ' ')}</Pill>
      </div>
      {a.note && <p className="iv-small" style={{ marginTop: '0.35rem' }}>“{a.note}”</p>}
      <div className="iv-small">{fmtDate(a.created_at)}{a.decided_note ? ` · Note: ${a.decided_note}` : ''}</div>
      {a.status === 'pending' && (
        <div className="iv-row" style={{ marginTop: '0.6rem' }}>
          <input className="iv-input" style={{ flex: 1, minWidth: 180 }} placeholder="Optional note" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`accommodation-note-${a.id}`} />
          <button type="button" className="iv-btn iv-btn-primary iv-btn-sm" onClick={() => decide('approved')} data-testid={`accommodation-approve-${a.id}`}>Approve</button>
          <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => decide('resolved')} data-testid={`accommodation-resolve-${a.id}`}>Mark resolved</button>
          <button type="button" className="iv-btn iv-btn-danger iv-btn-sm" onClick={() => decide('denied')} data-testid={`accommodation-deny-${a.id}`}>Deny</button>
        </div>
      )}
    </div>
  );
};

export function AccommodationsTab({ refreshKey, onChanged }) {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    aiInterviewsAPI.accommodations(status).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [status, refreshKey, tick]);
  return (
    <div data-testid="accommodations-tab">
      <div className="iv-chips">
        {[['pending', 'Pending'], ['all', 'All']].map(([k, l]) => (
          <button type="button" key={k} className={`iv-chip ${status === k ? 'is-on' : ''}`} onClick={() => setStatus(k)} data-testid={`accommodations-filter-${k}`}>{l}</button>
        ))}
      </div>
      {!rows && <Loading />}
      {rows && !rows.length && <Empty testId="accommodations-empty" title="No accommodation requests" body="Requests from candidates (extra time, captions, a human interview) appear here." />}
      {rows && rows.length > 0 && <div style={{ display: 'grid', gap: '0.6rem' }}>{rows.map((a) => <AccRow key={a.id} a={a} onDecided={() => { setTick((n) => n + 1); onChanged?.(); }} />)}</div>}
    </div>
  );
}
